import {
  Button,
  ButtonIcon,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  EmptyState,
  EmptyStateVisual,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from "@platform/ui/components";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import * as React from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { twMerge } from "tailwind-merge";
import { z } from "zod";
import { PARTICIPANTS_QUERIES } from "~/shared/api/participants/participants.queries";
import { ExternalSavedRole, InternalSavedRole, SavedRolesResponse } from "~/shared/api/participants/participants.types";
import { Pagination, PaginationStats } from "~/shared/components/ds/Pagination/Pagination";
import { Rank } from "~/shared/components/ds/Rank";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "~/shared/components/ds/Table";
import { ResponsiveDialogLayout } from "~/shared/components/ResponsiveDialogLayout";
import {
  Comp,
  NotesCellButton,
  NotesCellContentLayout,
  SavedRoleLogoCellContent,
  SavedRoleRoleCell,
} from "~/shared/components/SavedRoleCellElements";
import { MODULE_ICON } from "~/shared/constants/module-icon.constants";
import { useDeferredTransition } from "~/shared/hooks/useDeferredTransition";
import { getPageTotal, getRowEnd, getRowStart, getSkip } from "~/shared/util/pagination.helpers";
import { PARTNER_PATHS } from "~/team/constants/paths.constants";

export const MAX_RESULTS_PER_PAGE = 50;
export function ParticipantSavedRolesDataTable() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { participantId } = useParams();
  const pageParam = searchParams.get(SAVED_ROLES_SEARCH_PARAM_KEY.PAGE);
  const { page } = SavedRolesSearchParamSchema.parse({ page: pageParam });

  const params = React.useMemo(() => {
    return {
      params: {
        limit: MAX_RESULTS_PER_PAGE,
        skip: getSkip(page, MAX_RESULTS_PER_PAGE),
      },
    };
  }, [page]);
  const [isPending, deferredParams] = useDeferredTransition(params);
  const { data: savedRoles } = useSuspenseQuery(PARTICIPANTS_QUERIES.savedRolesList(participantId!, deferredParams));
  const { items: roles, total } = savedRoles ?? {};
  const pageTotal = getPageTotal(total, MAX_RESULTS_PER_PAGE);
  const rowNumStart = getRowStart(page, MAX_RESULTS_PER_PAGE, total);
  const rowNumEnd = getRowEnd(page, MAX_RESULTS_PER_PAGE, total);

  const table = useReactTable({
    columns: COLUMNS,
    data: roles,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row, _i) => `${row.id}`,
    manualSorting: true,
    manualPagination: true,
  });

  return (
    <div className="flex flex-col gap-6">
      <div className={twMerge("transition-opacity", isPending ? "cursor-wait opacity-50" : "")}>
        <Table
          className={twMerge(
            "grid grid-cols-[1fr_auto_60px] items-center transition-opacity lg:grid-cols-[56px_1fr_15%_25%_10%_60px]",
            isPending ? "pointer-events-none" : "",
          )}
        >
          <TableHeader className="z-0 contents">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="contents">
                {headerGroup.headers.map((header) => (
                  <React.Fragment key={header.id}>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </React.Fragment>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody className="contents">
            {table.getRowModel().rows?.length !== 0 ? (
              <>
                {table.getRowModel().rows.map((row) => {
                  return (
                    <TableRow key={row.id} className="contents">
                      {row.getVisibleCells().map((cell) => (
                        <React.Fragment key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </React.Fragment>
                      ))}
                    </TableRow>
                  );
                })}
              </>
            ) : (
              <TableRow className="contents">
                <TableCell className="col-span-full">
                  <EmptyState
                    visual={<EmptyStateVisual visual={<Icon className="size-full" symbol="database-2-line" />} />}
                    title="No Saved Roles found"
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="stack-x-6 flex-wrap justify-between">
        <PaginationStats rowNumStart={rowNumStart} rowNumEnd={rowNumEnd} total={total}>
          Saved Roles
        </PaginationStats>
        <Pagination
          currentPage={page}
          totalPages={pageTotal}
          siblingCount={1}
          onPageChange={(page) => {
            setSearchParams((searchParams) => {
              if (!page) {
                searchParams.delete(SAVED_ROLES_SEARCH_PARAM_KEY.PAGE);
                return searchParams;
              }

              searchParams.set(SAVED_ROLES_SEARCH_PARAM_KEY.PAGE, page);

              return searchParams;
            });
          }}
        />
      </div>
    </div>
  );
}

export const SAVED_ROLES_SEARCH_PARAM_KEY = {
  PAGE: "page",
} as const;
export const SavedRolesSearchParamSchema = z.object({
  page: z.coerce.number().int().positive().catch(1),
});

const columnHelper = createColumnHelper<SavedRolesResponse["items"][number]>();
const COLUMNS = [
  columnHelper.accessor((row) => row, {
    id: "logo",
    header: () => (
      <TableHead className="hidden items-center lg:flex">
        <span className="sr-only">Company Logo</span>
      </TableHead>
    ),
    cell: ({ getValue }) => {
      const row = getValue();

      return (
        <TableCell className="hidden self-start lg:table-cell">
          <SavedRoleLogoCellContent row={row} />
        </TableCell>
      );
    },
  }),
  columnHelper.accessor((row) => row, {
    id: "role",
    header: () => {
      return <TableHead className="flex items-center rounded-l-md lg:rounded-none">Role</TableHead>;
    },
    cell: ({ getValue }) => {
      const row = getValue();

      return <SavedRoleRoleCell row={row} />;
    },
  }),
  columnHelper.accessor((row) => ({ compensation: row.compensation, payType: row.payType }), {
    id: "compensation",
    header: () => {
      return <TableHead className="hidden items-center lg:flex">Compensation</TableHead>;
    },
    cell: ({ getValue }) => {
      const { compensation, payType } = getValue();

      return (
        <TableCell className="hidden lg:table-cell">
          <Comp comp={compensation} payType={payType} />
        </TableCell>
      );
    },
  }),
  columnHelper.accessor(
    (row) => ({
      notes: row.notes,
    }),
    {
      id: "notes",
      header: () => {
        return (
          <TableHead className="hidden items-center lg:flex">
            <span className="inline-block pl-2">Notes</span>
          </TableHead>
        );
      },
      cell: ({ getValue }) => {
        const { notes } = getValue();

        if (notes == null || notes.length <= 0) {
          return <TableCell className="hidden lg:table-cell" />;
        }

        return (
          <TableCell className="hidden lg:table-cell">
            <ViewNotesDialog notes={notes}>
              <NotesCellButton state="filled">
                <NotesCellContentLayout>{notes}</NotesCellContentLayout>
              </NotesCellButton>
            </ViewNotesDialog>
          </TableCell>
        );
      },
    },
  ),
  columnHelper.accessor(
    (row) => ({
      id: row.id,
      rank: row.rank,
      type: row.type,
    }),
    {
      id: "rank",
      header: () => {
        return <TableHead className="items-center lg:flex">Rank</TableHead>;
      },
      cell: ({ getValue }) => {
        const { rank } = getValue();

        return (
          <TableCell>
            <Rank level={rank} />
          </TableCell>
        );
      },
    },
  ),
  columnHelper.accessor((row) => row, {
    id: "actions",
    header: () => {
      return (
        <TableHead>
          <span className="sr-only">Actions</span>
        </TableHead>
      );
    },
    cell: ({ getValue }) => {
      const row = getValue();

      return (
        <TableCell className="justify-self-center lg:justify-self-end">
          <SavedRolesRowActions row={row} />
        </TableCell>
      );
    },
  }),
];

function ViewNotesDialog({ notes, children }: { notes: string; children: React.ReactNode }) {
  return (
    <ResponsiveDialogLayout
      title="Notes"
      dialogContent={() => {
        return <div>{notes}</div>;
      }}
      drawerContent={() => {
        return <div>{notes}</div>;
      }}
    >
      {children}
    </ResponsiveDialogLayout>
  );
}

function SavedRolesActionsLayout({ children }: { children?: React.ReactNode }) {
  return (
    <DropdownMenu>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button className="ml-auto" svgOnly variant="ghost">
                <ButtonIcon symbol="ri-more-fill" />
                <span className="sr-only">Open menu</span>
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipPortal>
            <TooltipContent side="bottom">Actions</TooltipContent>
          </TooltipPortal>
        </Tooltip>
      </TooltipProvider>

      <DropdownMenuContent align="end">
        <DropdownMenuGroup>
          <DropdownMenuLabel className="sr-only">List of actions for saved role</DropdownMenuLabel>
          {children}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SavedRolesRowActions({ row }: { row: InternalSavedRole | ExternalSavedRole }) {
  const { type, notes } = row;

  switch (type) {
    case "internal": {
      return (
        <SavedRolesActionsLayout>
          {row.job.url && row.job?.url?.length > 0 && (
            <DropdownMenuItem asChild>
              <Link
                className="no-underline focus-visible:ring-0"
                to={row.job.url}
                target="_blank"
                rel="noreferrer noopener"
              >
                <Icon className="size-4" aria-hidden symbol="external-link-line" />
                View
              </Link>
            </DropdownMenuItem>
          )}
          {row.company?.id && (
            <DropdownMenuItem asChild>
              <Link className="no-underline focus-visible:ring-0" to={PARTNER_PATHS.detail(row.company.id.toFixed())}>
                <Icon className="size-4" aria-hidden symbol={MODULE_ICON.partners} />
                Partner
              </Link>
            </DropdownMenuItem>
          )}
          {notes && notes?.length > 0 && (
            <ViewNotesDialog notes={notes ?? ""}>
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                }}
              >
                <Icon className="size-4" aria-hidden symbol="file-text-line" />
                Notes
              </DropdownMenuItem>
            </ViewNotesDialog>
          )}
        </SavedRolesActionsLayout>
      );
    }
    case "external": {
      return (
        <SavedRolesActionsLayout>
          {row.url && row.url?.length > 0 && (
            <DropdownMenuItem asChild>
              <Link
                className="no-underline focus-visible:ring-0"
                to={row.url}
                target="_blank"
                rel="noreferrer noopener"
              >
                <Icon className="size-4" aria-hidden symbol="external-link-line" />
                View
              </Link>
            </DropdownMenuItem>
          )}
          {notes && notes?.length > 0 && (
            <ViewNotesDialog notes={notes ?? ""}>
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                }}
              >
                <Icon className="size-4" aria-hidden symbol="file-text-line" />
                Notes
              </DropdownMenuItem>
            </ViewNotesDialog>
          )}
        </SavedRolesActionsLayout>
      );
    }

    default: {
      return null;
    }
  }
}
